import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { useState } from 'react'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Plus, Users } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useMembers } from '@/hooks'
import { UI } from '@/lib/constants'
import { MemberDialog } from '@/components/members/member-dialog'
import { MemberRow } from '@/components/members/member-row'
import { MemberFilters } from '@/components/members/member-filters'
import { AppPagination } from '@/components/shared/app-pagination'
import { EmptyState } from '@/components/shared/empty-state'
import { SortableHeader, type SortDirection } from '@/components/shared/sortable-header'
import type { Member } from '@/lib/api'

export const Route = createFileRoute('/_app/members')({
  component: MembersPage,
})

function TableRowSkeleton() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton className="h-4 w-16" />
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-20" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <Skeleton className="h-4 w-40" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-16" />
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <div className="flex gap-1">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-6 w-6" />
        </div>
      </TableCell>
      <TableCell>
        <Skeleton className="h-8 w-8" />
      </TableCell>
    </TableRow>
  )
}

function MembersPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({ search: '', status: 'all' })
  const [sortKey, setSortKey] = useState<string | null>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const {
    data: queryData,
    isLoading,
    error,
  } = useMembers(page, 20, filters, { sortBy: sortKey, sortDirection })
  const members = queryData?.data
  const meta = queryData?.meta

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters)
    setPage(1)
  }

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null)
    setSortDirection(direction)
    setPage(1)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-muted-foreground">Erro ao carregar membros</p>
          <p className="text-sm text-destructive">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Membros"
        description="Gerencie os indicadores do seu programa"
        actions={
          <ChonkyButton
            variant="primary"
            onClick={() => {
              setSelectedMember(null)
              setIsDialogOpen(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Novo Membro
          </ChonkyButton>
        }
      />

      <MemberFilters filters={filters} onFiltersChange={handleFiltersChange} />

      <div className="max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-theme(spacing.64)-3rem)]">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                sortKey="createdAt"
                currentSort={sortKey}
                currentDirection={sortDirection}
                onSort={handleSort}
                className="w-24"
              >
                Criado
              </SortableHeader>
              <TableHead>Nome</TableHead>
              <TableHead>Código</TableHead>
              <SortableHeader
                sortKey="balance"
                currentSort={sortKey}
                currentDirection={sortDirection}
                onSort={handleSort}
              >
                Saldo
              </SortableHeader>
              <TableHead className="hidden md:table-cell">Email</TableHead>
              <SortableHeader
                sortKey="status"
                currentSort={sortKey}
                currentDirection={sortDirection}
                onSort={handleSort}
              >
                Status
              </SortableHeader>
              <TableHead className="hidden sm:table-cell w-20 text-center">Integrações</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: UI.SKELETON_TABLE_ROWS }).map((_, i) => (
                <TableRowSkeleton key={i} />
              ))
            ) : members?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  {filters.search || filters.status !== 'all' ? (
                    <EmptyState
                      icon={Users}
                      title="Nenhum resultado encontrado"
                      description="Tente ajustar os filtros para encontrar o que procura."
                      actionLabel="Limpar filtros"
                      onAction={() => handleFiltersChange({ search: '', status: 'all' })}
                      className="sticky left-0 w-[calc(100vw-2rem)] md:w-[calc(100vw-theme(spacing.64)-3rem)]"
                    />
                  ) : (
                    <EmptyState
                      icon={Users}
                      title="Nenhum membro cadastrado"
                      description="Adicione seu primeiro membro para começar a usar o programa de indicações."
                      actionLabel="Adicionar membro"
                      onAction={() => {
                        setSelectedMember(null)
                        setIsDialogOpen(true)
                      }}
                      className="sticky left-0 w-[calc(100vw-2rem)] md:w-[calc(100vw-theme(spacing.64)-3rem)]"
                    />
                  )}
                </TableCell>
              </TableRow>
            ) : (
              members?.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  onEdit={(m) => {
                    setSelectedMember(m)
                    setIsDialogOpen(true)
                  }}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AppPagination meta={meta} onPageChange={setPage} isLoading={isLoading} />

      <MemberDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} member={selectedMember} />
    </div>
  )
}
